/* eslint-disable */
import * as THREE from 'three';
import { gsap } from 'gsap';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

import Component from '../../components/component/component';
import Helpers from '../../components/helpers/helpers';
import './house-3d.scss';
import { event } from 'jquery';

export default class House3D extends Component {
    static initSelector: string = '.js-house-3d';

    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    labelRenderer: CSS2DRenderer;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    apartments: ReturnType<typeof Object>[];
    rawApartments: ReturnType<typeof Object>[];
    raycaster: THREE.Raycaster;
    mouse: THREE.Vector2;
    orbitMouse: THREE.Vector2;
    sprite: THREE.Sprite;
    hoveringObject: null|ReturnType<typeof Object>;
    cameraPositions: Array<THREE.Vector3>;
    angle: number = 0;
    gsapMovement: GSAPTween;
    textures: ReturnType<typeof Object>[];
    materials: ReturnType<typeof Object>;
    config: ReturnType<typeof Object>;

    constructor(target: HTMLElement) {
        super(target);

        this.init();
    }

    init(): void {
        this.apartments = [];
        this.orbitMouse = new THREE.Vector2();
        this.materials = {};
        this.config = {};
        this.textures = [];

        this.loadModelConfig(() => {
            this.setupScene();
            this.setupRenderer();
            this.setupCameraAndControls();
            this.setupLighting();
            this.setupRaycaster();

            this.loadApartments(() => {
                this.loadTextures();
                this.loadModelsAndMaterials();
            });

        })

        window.addEventListener('resize', () => this.onWindowResize());
    }

    getConfigValue(key: string, defaultValue: any) {
        const value = this.config.hasOwnProperty(key) ? this.config[key] : defaultValue

        if (['cameraFov', 'cameraFar', 'lightIntensity', 'lightDirectionIntensity', 'landscapeChannel', 'mainChannel'].includes(key)) {
            return parseFloat(value)
        } else if (['castShadow', 'applyGeneralWhiteMaterial'].includes(key)) {
            return !!+value
        }

        return value
    }

    loadModelConfig(callback): void {
        let configUrl: string = this.element.find('.house-3d-container').data('model-config-url');

        if (configUrl) {
            $.get(
                configUrl,
                (data) => {
                    if (data) {
                        this.config = data;

                        callback();
                    }
                },
                'JSON'
            );
        } else {
            callback();
        }
    }

    loadApartments(callback): void {
        $.ajax({
            data: {
                action: 'get_house_apartments',
                houseId: this.element.children('.house-3d-container').data('id'),
                security: window.gotoAndPlay.nonce,
            },
            url: window.gotoAndPlay.ajaxPath,
            type: 'POST',
            dataType: 'JSON',
            success: (response) => {
                if (response.success) {
                    this.rawApartments = response.data;
                }

                callback();
            }
        });
    }

    setupScene(): void {
        this.scene = new THREE.Scene();
    }

    setupRenderer(): void {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.element.children('.house-3d-container').innerWidth(), this.element.children('.house-3d-container').innerHeight());
        this.renderer.setClearColor(0xffffff, 0);
        this.element.find('.house-3d-container').append(this.renderer.domElement);

        this.labelRenderer = new CSS2DRenderer()
        this.labelRenderer.setSize(this.element.children('.house-3d-container').innerWidth(), this.element.children('.house-3d-container').innerHeight());
        this.labelRenderer.domElement.classList.add('house-3d-container__labels');
        this.element.find('.house-3d-container').append(this.labelRenderer.domElement);

        this.renderer.setAnimationLoop(() => this.animate())
    }

    setupCameraAndControls(): void {
        // Camera
        this.camera = new THREE.PerspectiveCamera(50, this.element.children('.house-3d-container').innerWidth() / this.element.children('.house-3d-container').innerHeight(), 0.1, 2000);
        this.camera.position.set(0, 0, 0);

        // Controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.maxPolarAngle = Math.PI / 2;
        this.controls.enablePan = false;
        this.controls.enableZoom = false; // disable zoom

        this.controls.maxAzimuthAngle = this.controls.getAzimuthalAngle()
    }

    setupLighting(): void {
        const ambientLight: THREE.AmbientLight = new THREE.AmbientLight(0xffffff, this.getConfigValue('ambientLightIntensity', 0.5));

        this.scene.add(ambientLight);

        const directionalLight: THREE.DirectionalLight = new THREE.DirectionalLight(this.getConfigValue('directionalLightColor', 0xffffff), 1);

        directionalLight.position.set(0, 1, 1).normalize();
        this.scene.add(directionalLight);
    }

    setupRaycaster(): void {
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
    }

    loadTextures(): void {
        const textureLoader = new THREE.TextureLoader()

        this.materials.doors = []
        this.materials.cubes = []

        const modelTextures = this.element.find('.house-3d-container').data('model-textures')

        if (modelTextures && Array.isArray(modelTextures)) {
            this.textures = modelTextures
        }

        this.textures.forEach(textureUrl => {
            const filename = textureUrl.split('/').pop()

            if (textureUrl.includes('cubemap/')) {
                this.materials.cubes.push(textureUrl)

                return;
            }

            let texture = textureLoader.load(textureUrl)

            if (filename.startsWith('door_')) {
                texture.flipY = false
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

                const doorMaterial = new THREE.MeshStandardMaterial({
                    color: 'rgb(51, 51, 51)',
                    transparent: true,
                    side: THREE.DoubleSide,
                    alphaMap: texture,
                    depthTest: true,
                    roughness: 0.2,
                    metalness: 0.2,
                    depthWrite: false,
                })

                this.materials.doors.push(doorMaterial)
            } else if (filename.startsWith('main')) {
                texture.flipY = false
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.colorSpace = THREE.SRGBColorSpace
                texture.mapping = THREE.EquirectangularReflectionMapping

                this.materials.main = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('solar')) {
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.solar = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('trunk')) {
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.colorSpace = THREE.SRGBColorSpace
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);

                this.materials.trunk = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('wood')) {
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.wood = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('brick')) {
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.brick = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('corten')) {
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.colorSpace = THREE.SRGBColorSpace
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

                this.materials.corten = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                    roughness: 0.2,
                    metalness: 0.2,
                })
            } else if (filename.startsWith('window_arches')) {
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.colorSpace = THREE.SRGBColorSpace
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

                this.materials.windowArches = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('alpha_bush_01.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush01 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                });
            } else if (filename.startsWith('alpha_bush_01a.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush01.alphaMap = texture;
            } else if (filename.startsWith('alpha_bush_02.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush02 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                });
            } else if (filename.startsWith('alpha_bush_02a.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush02.alphaMap = texture;
            } else if (filename.startsWith('alpha_bush_03.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush03 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                });

                this.materials.bush05 = this.materials.bush03.clone();
            } else if (filename.startsWith('alpha_bush_03a.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush03.alphaMap = texture;
            } else if (filename.startsWith('alpha_bush_04.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush04 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                });
            } else if (filename.startsWith('alpha_bush_04a.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bush04.alphaMap = texture;
            } else if (filename.startsWith('alpha_bush_05a.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                if (this.materials.bush05) {
                    this.materials.bush05.alphaMap = texture;
                }
            } else if (filename.startsWith('bush_alpha')) {
                this.materials.bushAlpha = texture

                if (this.materials.bush) {
                    this.materials.bush.alphaMap = texture
                }
            } else if (filename.startsWith('bush')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false

                this.materials.bush = new THREE.MeshMatcapMaterial({
                    transparent: true,
                    matcap: texture,
                    side: THREE.DoubleSide,
                    depthWrite: false,
                    alphaTest: 0.5,
                })

                if (this.materials.bushAlpha) {
                    this.materials.bush.alphaMap = this.materials.bushAlpha
                }
            } else if (filename.startsWith('bollard')) {
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bollard = new THREE.MeshMatcapMaterial({
                    matcap: texture,
                })
            } else if (filename.startsWith('glass')) {
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.glass = new THREE.MeshMatcapMaterial({
                    matcap: texture,
                    opacity: 0.8,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('black')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.blackMetal = new THREE.MeshStandardMaterial({
                    map: texture,
                    roughness: 0.4,
                    metalness: 0.2,
                })
            } else if (filename.startsWith('standard_black')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.black = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('standard_white')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.white = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                    opacity: 0.8,
                })
            } else if (filename.startsWith('orange')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.orange = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('grey')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.grey = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('signs')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

                this.materials.sign = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('landscape')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.minFilter = THREE.LinearMipmapLinearFilter
                texture.magFilter = THREE.LinearFilter
                texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy()

                if (this.getConfigValue('landscapeChannel', false)) {
                    texture.channel = this.getConfigValue('mainChannel', 1)
                }

                this.materials.landscape = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('decking')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.decking = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('terrace')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.terrace = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('baked') && ! filename.startsWith('bakedwindows')) {
                texture.colorSpace = THREE.SRGBColorSpace
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.baked = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('rail')) {
                texture.flipY = false

                this.materials.railing = new THREE.MeshStandardMaterial({
                    transparent: true,
                    alphaMap: texture,
                    color: 'rgb(51, 51, 51)',
                    depthWrite: false,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('rooftop')) {
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.rooftop = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('basic')) {
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)

                this.materials.basic = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('env')) {
                texture.mapping = THREE.EquirectangularReflectionMapping

                this.materials.env = texture

                if (this.materials.glass) {
                    this.materials.glass.envMap = texture
                    this.materials.glass.envMapIntensity = 2
                }
            } else if (filename.startsWith('bakedwindows.')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bakedWindowsTexture = texture
            } else if (filename.startsWith('bakedwindows_alpha')) {
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace

                this.materials.bakedWindowsAlphaTexture = texture
            } else if (filename.startsWith('bits')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.bits = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('building')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.building = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('stone')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.stone = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('light')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.light = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('intwalls')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.intwalls = new THREE.MeshBasicMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                })
            } else if (filename.startsWith('champagne')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.channel = this.getConfigValue('mainChannel', 1)
                texture.anisotropy = 100
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.champagne = new THREE.MeshStandardMaterial({
                    map: texture,
                    side: THREE.DoubleSide,
                    metalness: 0.5,
                    roughness: 0.5,
                })
            } else if (filename.startsWith('tree09a')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false

                if (this.materials.tree009) {
                    this.materials.tree009.alphaMap = texture
                } else {
                    this.materials.tree009AlphaTexture = texture
                }
            } else if (filename.startsWith('tree09')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.tree009 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                })

                if (this.materials.tree009AlphaTexture) {
                    this.materials.tree009.alphaMap = this.materials.tree009AlphaTexture
                }
            } else if (filename.startsWith('tree11a')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false

                if (this.materials.tree011) {
                    this.materials.tree011.alphaMap = texture
                } else {
                    this.materials.tree011AlphaTexture = texture
                }
            } else if (filename.startsWith('tree11')) {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                texture.flipY = false
                texture.colorSpace = THREE.SRGBColorSpace;

                this.materials.tree011 = new THREE.MeshBasicMaterial({
                    map: texture,
                    alphaTest: 0.5,
                    transparent: true,
                    side: THREE.DoubleSide,
                })

                if (this.materials.tree011AlphaTexture) {
                    this.materials.tree011.alphaMap = this.materials.tree011AlphaTexture
                }
            }
        })

        this.materials.ghost = new THREE.MeshStandardMaterial({
            color: 0xf5ede5,
            side: THREE.FrontSide,
            transparent: true,
            opacity: 0.5,
            userData: {
                edgeOutline: {
                    color: 0x999694,
                    threshold: 15,
                },
            },
        })

        this.materials.tree = new THREE.MeshMatcapMaterial({
            color: 'rgb(177, 163, 144)',
        })

        this.materials.generalBlack = new THREE.MeshStandardMaterial({
            color: 'rgb(49, 49, 49)',
        })

        this.materials.generalGrey = new THREE.MeshStandardMaterial({
            color: 'rgb(24, 24, 24)',
        })

        this.materials.generalWhite = new THREE.MeshStandardMaterial({
            color: this.getConfigValue('whiteColor', 'rgb(173, 173, 173)'),
            opacity: this.getConfigValue('whiteOpacity', 1),
        })

        const whiteType: string = this.getConfigValue('whiteType', 'meshStandard')

        if (whiteType == 'meshStandard') {
            this.materials.generalWhite = new THREE.MeshStandardMaterial({
                color: this.getConfigValue('whiteColor', 'rgb(173, 173, 173)'),
                opacity: this.getConfigValue('whiteOpacity', 1),
            })
        } else if (whiteType == 'meshBasic') {
            this.materials.generalWhite = new THREE.MeshBasicMaterial({
                color: this.getConfigValue('whiteColor', 'rgb(173, 173, 173)'),
                opacity: this.getConfigValue('whiteOpacity', 1),
                side: THREE.DoubleSide,
            })
        }

        if (! this.materials.sign) {
            this.materials.sign = new THREE.MeshStandardMaterial({
                color: 'rgb(255, 255, 255)',
                emissive: 'rgb(255, 255, 255)',
            })
        }

        this.materials.pos4 = new THREE.MeshStandardMaterial({
            color: 'rgb(167, 211, 252)',
            transparent: true,
            opacity: 0.75,
            roughness: 0.5,
            metalness: 0.5,
        })

        this.materials.myfitnessWhite = new THREE.MeshBasicMaterial({
            transparent: true,
            alphaTest: 0.01,
            color: 'rgb(121, 121, 121)',
        })

        this.materials.myfitnessRed = new THREE.MeshBasicMaterial({
            color: 'rgb(163, 17, 17)',
            side: THREE.DoubleSide,
        })

        this.materials.themeGreen = new THREE.MeshBasicMaterial({
            color: '#9cb69c',
            side: THREE.DoubleSide,
        });

        this.materials.themePastel = new THREE.MeshBasicMaterial({
            color: '#745c54',
            side: THREE.DoubleSide,
        });

        if (! this.materials.glass) {
            let configGlass = this.getConfigValue('glassMaterial', {
                color: 0xc1e1ff,
                transparent: true,
                opacity: 0.4,
                metalness: 1.0,
                roughness: 0.0,
                depthWrite: false,
                alphaTest: 0.1,
            })

            if (configGlass.type && configGlass.type == 'meshStandard') {
                delete configGlass.type;

                this.materials.glass = new THREE.MeshStandardMaterial(configGlass);
            } else {
                if (configGlass.type) {
                    delete configGlass.type;
                }

                this.materials.glass = new THREE.MeshPhysicalMaterial(configGlass);
            }

            if (this.materials.env) {
                this.materials.glass.envMap = this.materials.env

                if (! this.materials.glass.envMapIntensity) {
                    this.materials.glass.envMapIntensity = 2
                }
            }
        }

        if (this.materials.cubes.length > 0) {
            const cubeMap = (new THREE.CubeTextureLoader()).load(this.materials.cubes);

            if (this.materials.bakedWindowsTexture) {
                this.materials.bakedWindows = new THREE.ShaderMaterial({
                    uniforms: {
                        u_maskTexture: { value: this.materials.bakedWindowsAlphaTexture },
                        u_frameTexture: { value: this.materials.bakedWindowsTexture },
                        u_envMap: { value: cubeMap },
                        u_cameraPosition: { value: new THREE.Vector3() },
                        u_transparency: { value: 0.8 },  // glass alpha value
                        u_glassTint: { value: new THREE.Color(0.75, 0.75, 0.7) } // subtle tint
                    },
                    vertexShader: `
                        varying vec2 vUv;
                        varying vec3 vWorldNormal;
                        varying vec3 vWorldPosition;

                        void main() {
                        vUv = uv;
                        vWorldNormal = normalize(normalMatrix * normal);
                        vec4 worldPos = modelMatrix * vec4(position, 1.0);
                        vWorldPosition = worldPos.xyz;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                        }
                    `,
                    fragmentShader: `
                        uniform sampler2D u_maskTexture;
                        uniform sampler2D u_frameTexture;
                        uniform samplerCube u_envMap;
                        uniform vec3 u_cameraPosition;
                        uniform float u_transparency;
                        uniform vec3 u_glassTint;

                        varying vec2 vUv;
                        varying vec3 vWorldNormal;
                        varying vec3 vWorldPosition;

                        void main() {
                        float mask = 1.0 - texture2D(u_maskTexture, vUv).g; // invert: black = glass, white = frame

                        vec3 viewDir = normalize(vWorldPosition - u_cameraPosition);
                        vec3 reflectDir = reflect(viewDir, normalize(vWorldNormal));

                        // Frame texture (linearized from sRGB)
                        vec3 frameColor = pow(texture2D(u_frameTexture, vUv).rgb, vec3(1));

                        // CubeMap reflection with tint
                        vec3 reflectionColor = textureCube(u_envMap, reflectDir).rgb;
                        reflectionColor = mix(reflectionColor, u_glassTint, 0.5);

                        // Blend colors and alpha based on mask
                        vec3 finalColor = mix(frameColor, reflectionColor, mask);
                        float alpha = mix(1.0, u_transparency, mask); // 1 = opaque frame, transparency = glass

                        gl_FragColor = vec4(finalColor, alpha);
                        }
                    `,
                    side: THREE.DoubleSide,
                    transparent: true,
                })
            }
        }

        this.materials.pos11Default = new THREE.MeshBasicMaterial({
            color: 0x000000,
            side: THREE.DoubleSide,
            opacity: 0.8,
        });
    }

    loadModelsAndMaterials(): void {
        const loader: GLTFLoader = new GLTFLoader();
        const dracoLoader: DRACOLoader = new DRACOLoader();
        let modelUrl: string = this.element.find('.house-3d-container').data('model-url');

        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')
        loader.setDRACOLoader(dracoLoader)

        if (window.matchMedia('(max-width: ' + Helpers.bp.sm + 'px)').matches) {
            const mobileUrl = this.element.find('.house-3d-container').data('model-mobile-url');

            if (mobileUrl) {
                modelUrl = mobileUrl;
            }
        }

        loader.load(
            modelUrl,
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (gltf: any) => {
                const model: THREE.Group = gltf.scene;

                const box = new THREE.Box3().setFromObject(model)

                const boxCenter = box.getCenter(new THREE.Vector3())
                const boxSize = box.getSize(new THREE.Vector3())

                model.position.set(-boxCenter.x, boxSize.y / 2 - boxCenter.y, -boxCenter.z);

                this.scene.add(model);

                this.applyMaterialsToModel(model);
                this.adjustCameraToFitModel(model);
                this.setApartmentClosestCamera();
                this.hideLoaderShowCanvas();
                this.animateInitialCamera();
                this.addMouseEvents();
            },
            (xhr: ProgressEvent) => this.updateLoadingBar(xhr),
        );
    }

    updateLoadingBar(xhr: ProgressEvent): void {
        //
    }

    getMaterialMappings(): ReturnType<typeof Object> {
        return {
            'MAIN': this.materials.main,
            'WALL_MAIN': this.materials.main,
            'WALL_CORTEN': this.materials.corten,
            'GLASS': this.materials.glass,
            'BAKEDWINDOWS': this.materials.bakedWindows,
            'BOLLARD': this.materials.bollard,
            'LANDSCAPE_BASE': this.materials.landscape,
            'BLACKSTUFF': this.materials.blackMetal,
            'BLACKRAILS': this.materials.pos11Default,
            'BLACK': this.materials.black,
            'WHITE': this.materials.white,
            'GREY': this.materials.grey,
            'ORANGE': this.materials.orange,
            'POS4': this.materials.pos4,
            'DECKING': this.materials.decking,
            'RAILING': this.materials.railing,
            'WINDOW_ARCHES': this.materials.windowArches,
            'SIGN_ILLUMINATED': this.materials.sign,
            'SIGNS': this.materials.sign,
            'TERRACE_STUFF_COLLAPSED': this.materials.terrace,
            'MYFITNESS_01': this.materials.myfitnessWhite,
            'MYFITNESS_02': this.materials.myfitnessWhite,
            'MYFITNESS_03': this.materials.myfitnessRed,
            'LANDSCAPE': this.materials.landscape,
            'BASIC': this.materials.basic,
            'NOTES': this.materials.generalWhite,
            'SOLAR': this.materials.solar,
            'TRUNK': this.materials.tree,
            'WOOD': this.materials.wood,
            'BRICK': this.materials.brick,
            'ROOFTOP': this.materials.rooftop,
            'TREE009': this.materials.tree009,
            'TREE011': this.materials.tree011,
            'TREE': this.materials.tree,
            'UMBRELLA': this.materials.generalWhite,
            'POT': this.materials.generalBlack,
            'CHAIR': this.materials.bollard,
            'TABLE': this.materials.bollard,
            'LEAVES': this.materials.bush,
            'BUSH01': this.materials.bush01,
            'BUSH02': this.materials.bush02,
            'BUSH03': this.materials.bush03,
            'BUSH04': this.materials.bush04,
            'BUSH05': this.materials.bush05,
            'BUSH06': this.materials.bush02,
            'BUSH': this.materials.bush,
            'STONE': this.materials.stone,
            'BIKE': this.materials.generalGrey,
            'BITS': this.materials.bits,
            'BUILDING': this.materials.building,
            'LIGHT': this.materials.light,
            'INTWALLS': this.materials.intwalls,
            'CHAMPAGNE': this.materials.champagne,
            'GHOST': this.materials.ghost,
            'BAKED': this.materials.baked,
        }
    }

    applyMaterialsToModel(model: THREE.Group): void {
        const materialMappings = this.getMaterialMappings();
        const materialInstances = {
            BUSH01: { instances: [], geometry: null, material: materialMappings.BUSH01 },
            BUSH02: { instances: [], geometry: null, material: materialMappings.BUSH02 },
            BUSH06: { instances: [], geometry: null, material: materialMappings.BUSH06 },
            BUSH03: { instances: [], geometry: null, material: materialMappings.BUSH03 },
            BUSH04: { instances: [], geometry: null, material: materialMappings.BUSH04 },
            BUSH05: { instances: [], geometry: null, material: materialMappings.BUSH05 },
        }

        console.log(this.materials)

        model.traverse((child) => {
            const mesh = child as THREE.Mesh;

            if (! mesh.isMesh) {
                return;
            }

            if (mesh.name.startsWith('BBOX') || mesh.name.startsWith('APARTMENT_') || mesh.name.startsWith('pos16') || mesh.name.startsWith('apt_') || mesh.name.startsWith('apartment_')) {
                this.setupApartmentObject(mesh);
            } else {
                const materialKey = Object.keys(materialMappings).find(key => (mesh.name.includes(key) && ! mesh.name.startsWith('BUSH')) || (mesh.name.includes(key + '_') && mesh.name.startsWith('BUSH'))) || 'DEFAULT';

                if (materialInstances[materialKey]) {
                    const group = materialInstances[materialKey];

                    if (! group.geometry) {
                        group.geometry = mesh.geometry;
                    }

                    group.instances.push(mesh);
                    mesh.visible = false;

                    return;
                }

                if (materialKey && Object.keys(materialMappings).includes(materialKey) && materialMappings[materialKey]) {
                    mesh.material = materialMappings[materialKey];

                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((material) => {
                            material.needsUpdate = true;
                        });
                    } else {
                        mesh.material.needsUpdate = true;
                    }

                    if (materialKey.includes('GLASS')) {
                        mesh.renderOrder = 0
                    }

                } else if (mesh.name.startsWith('DOOR')) {
                    const doorIndex = parseInt(mesh.name.split('_')[1], 10) - 1

                    if (this.materials.doors[doorIndex]) {
                        mesh.material = this.materials.doors[doorIndex]
                    }
                } else if (mesh.name.startsWith('BUSH')) {
                    const bushMaterial = this.materials.bush.clone()
                    const baseGreen = 0.25
                    const greenVariation = (Math.random() - 0.15) * 0.5
                    bushMaterial.color.setRGB(0.1, Math.min(1, Math.max(0, baseGreen + greenVariation)), 0.25)

                    mesh.material = bushMaterial;
                    mesh.renderOrder = 1
                } else if (mesh.name.startsWith('LABELS')) {
                    if (document.body.classList.contains('theme-light-green')) {
                        mesh.material = this.materials.themeGreen;
                    } else {
                        mesh.material = this.materials.themePastel;
                    }
                } else {
                    if (this.getConfigValue('applyGeneralWhiteMaterial', true)) {
                        mesh.material = this.materials.generalWhite;

                        if (Array.isArray(mesh.material)) {
                            mesh.material.forEach((material) => {
                                material.needsUpdate = true;
                            });
                        } else {
                            mesh.material.needsUpdate = true;
                        }
                    }

                    console.log('No material mapping found for mesh:', mesh.name);
                }

                const activeMaterial = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
                const outlineCfg = activeMaterial?.userData?.edgeOutline;

                if (outlineCfg && !mesh.userData.__edgeLines) {
                    try {
                        const threshold = Number.isFinite(outlineCfg.threshold) ? outlineCfg.threshold : 15;
                        const edgeGeom = new THREE.EdgesGeometry(mesh.geometry, threshold);
                        const edgeMat = new THREE.LineBasicMaterial({ color: outlineCfg.color ?? 0xffffff });
                        const edges = new THREE.LineSegments(edgeGeom, edgeMat);
                        edges.name = `${mesh.name}_edges`;
                        edges.matrixAutoUpdate = false;
                        edges.renderOrder = (mesh.renderOrder || 0) + 1;

                        mesh.add(edges);
                        mesh.userData.__edgeLines = edges;
                    } catch (e) {
                        console.warn("Edge overlay failed for", mesh.name, e);
                    }
                }
            }
        });

        Object.values(materialInstances).forEach(({ instances, geometry, material }) => {
            if (instances.length > 0 && geometry && material) {
                const instancedMesh = new THREE.InstancedMesh(geometry, material, instances.length);

                instances.forEach((child, i) => {
                    child.updateMatrixWorld();

                    let tempObj = new THREE.Object3D();

                    tempObj.position.setFromMatrixPosition(child.matrixWorld);
                    tempObj.rotation.setFromRotationMatrix(child.matrixWorld);
                    tempObj.scale.copy(child.scale);
                    tempObj.updateMatrix();

                    instancedMesh.setMatrixAt(i, tempObj.matrix);
                });

                model.add(instancedMesh);
            }
        })
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    setupApartmentObject(data: any): void {
        const dataNameSplit = data.name.split('_')

        data.material = new THREE.MeshStandardMaterial({
            color: 0x0000ff,
            transparent: true,
            opacity: 0,
            depthTest: false,
            depthWrite: false,
        });
        data.renderOrder = -1

        if (dataNameSplit.length > 1) {
            let apartmentNumber: any = parseFloat(dataNameSplit[dataNameSplit.length - 1])

            if (this.getConfigValue('threeCharUnits', false) == false) {
                if (dataNameSplit.length == 3) {
                    // Probably splitted apartment.
                    apartmentNumber = parseInt(dataNameSplit[dataNameSplit.length - 2]) + '.' + apartmentNumber;
                } else if (dataNameSplit[dataNameSplit.length - 1].length == 3 && dataNameSplit[dataNameSplit.length - 1].substring(0, 1) == '0' && dataNameSplit[dataNameSplit.length - 1].substring(1, 2) != '0') {
                    // Probably splitted apartment.
                    apartmentNumber = dataNameSplit[dataNameSplit.length - 1].substring(1, 2) + '.' + dataNameSplit[dataNameSplit.length - 1].substring(2, 3);
                }
            }

            data.apartment = this.rawApartments.find(apartment => parseFloat(apartment.num) == apartmentNumber)

            if (! data.apartment) {
                console.log(apartmentNumber)

                return
            }

            const price = data.apartment.price;
            const number = data.apartment.tooltipNum.toUpperCase();
            const rooms = data.apartment.rooms;
            const size = data.apartment.size;

            let tooltipHtml = '<div class="house-3d-tooltip">';
            tooltipHtml += '<div class="h3 house-3d-tooltip__title"></div>';
            tooltipHtml += '<div class="house-3d-tooltip__content house-3d-tooltip__rooms"></div>';
            tooltipHtml += '<div class="house-3d-tooltip__content house-3d-tooltip__size"></div>';
            tooltipHtml += '<div class="house-3d-tooltip__content house-3d-tooltip__price"></div>';
            tooltipHtml += '</div>';

            const tooltipDiv = $(tooltipHtml);
            tooltipDiv.find('.house-3d-tooltip__title').html(number);
            tooltipDiv.find('.house-3d-tooltip__price').html(price);
            tooltipDiv.find('.house-3d-tooltip__rooms').text(rooms + ' ' + window.gotoAndPlay.i18n.rooms);
            tooltipDiv.find('.house-3d-tooltip__size').html(size + ' <span>m<sup>2</sup></span>');

            const tooltipObject = new CSS2DObject(tooltipDiv.get(0));
            tooltipObject.center.set(0.5, 1.4);

            data.add(tooltipObject);
            this.apartments.push(data);
        }
    }

    setApartmentClosestCamera() {
        const definedPositions = this.getConfigValue('cameraPositions', [])

        this.apartments.forEach(apartment => {
            if (definedPositions.length > 0) {
                const currentCameraPosition = definedPositions.find(position => position.num === apartment.apartment.num);

                if (currentCameraPosition) {
                    apartment.closestCamera = new THREE.Vector3(
                        parseFloat(currentCameraPosition.target[0]),
                        parseFloat(currentCameraPosition.target[1]),
                        parseFloat(currentCameraPosition.target[2])
                    );
                }
            }

            if (! apartment.closestCamera) {
                // Find the closest camera position to the apartment
                let closestCameraPosition = this.cameraPositions[0];
                let minDistance = apartment.position.distanceTo(closestCameraPosition);

                // Get apartment center position
                const apartmentBox = new THREE.Box3().setFromObject(apartment);
                const apartmentPosition = apartmentBox.getCenter(new THREE.Vector3());

                for (const position of this.cameraPositions) {
                    const distance = apartmentPosition.distanceTo(position);

                    if (distance < minDistance) {
                        minDistance = distance;
                        closestCameraPosition = position;
                    }
                }

                // Set apartment
                apartment.closestCamera = closestCameraPosition
            }
        })
    }

    // Adjust camera position and controls to focus on the loaded model
    adjustCameraToFitModel(model: THREE.Group): void {
        const box: THREE.Box3 = new THREE.Box3().setFromObject(model);
        let center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const distance: number = size.length() * this.getConfigValue('cameraDistanceFactor', 0.55);
        const distanceY: number = box.max.y * this.getConfigValue('cameraDistanceYFactor', 0.9);
        const centerPoint: string = this.getConfigValue('centerPoint', false);

        if (centerPoint) {
            // If centerPoint is set, use it as the center
            center = new THREE.Vector3(
                parseFloat(centerPoint.split(',')[0]),
                parseFloat(centerPoint.split(',')[1]),
                parseFloat(centerPoint.split(',')[2])
            );
        }

        // Define six camera positions around the model
        this.cameraPositions = [
            new THREE.Vector3(center.x, distanceY, center.z - distance * 1.2), // Front
            new THREE.Vector3(center.x + distance * 1.2, distanceY, center.z - distance * 0.5), // Front left
            new THREE.Vector3(center.x - distance * 1.2, distanceY, center.z - distance * 0.5), // Front right
            new THREE.Vector3(center.x, distanceY, center.z + distance * 1.2), // Back
            new THREE.Vector3(center.x + distance * 1.2, distanceY, center.z + distance * 0.5), // Back Left
            new THREE.Vector3(center.x - distance * 1.2, distanceY, center.z + distance * 0.5), // Back right
        ];

        this.cameraPositions.forEach(position => {
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
            const cube = new THREE.Mesh(geometry, material);

            // Hide the cube
            cube.visible = false

            cube.position.copy(position)

            this.scene.add(cube)
        })

        this.camera.position.copy(this.cameraPositions[0]);

        const initialDistance = this.camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
        this.controls.minDistance = initialDistance * 0.85;
        this.controls.maxDistance = initialDistance * 1.1;

        // Set initial camera position a bit offset for animation
        this.camera.position.x += distance;
        this.camera.position.y += 20;
        this.camera.position.z -= 5;
        this.camera.lookAt(center);

        this.controls.target.copy(center);
        this.controls.update();
    }

    animateInitialCamera() {
        gsap.timeline().to(this.camera.position, {
            x: this.cameraPositions[0].x,
            y: this.cameraPositions[0].y,
            z: this.cameraPositions[0].z,
            duration: 1,
            onUpdate: () => this.camera.lookAt(new THREE.Vector3(0, 0, 0))
        })
    }

    addMouseEvents() {
        document.addEventListener('debugCameraLocation', (event: CustomEvent) => {
            let entry = {
                num: this.hoveringObject.apartment.num,
                target: [this.camera.position.x, this.camera.position.y, this.camera.position.z],
            }

            if (this.hoveringObject && this.hoveringObject.apartment) {
                entry.num = this.hoveringObject.apartment.num
            }

            console.log(entry, JSON.stringify(entry))
        });

        this.renderer.domElement.addEventListener('pointermove', (event: PointerEvent) => this.onMouseMove(event));

        this.renderer.domElement.addEventListener('mousedown', (event: PointerEvent) => {
            this.orbitMouse.x = event.clientX;
            this.orbitMouse.y = event.clientY;
        })

        this.renderer.domElement.addEventListener('click', (event: PointerEvent) => {
            if (this.orbitMouse.x == event.clientX && this.orbitMouse.y == event.clientY) {
                this.onApartmentClick(event);
            }
        })

        this.controls.addEventListener('start', () => {
            if (this.gsapMovement) {
                this.gsapMovement.pause();
                this.gsapMovement.kill();
            }
        })

        document.addEventListener('enterApartmentTableRow', (event: CustomEvent) => this.maybeRotateToApartment(event))
        document.addEventListener('leaveApartmentTableRow', (event: CustomEvent) => this.maybeHideApartment(event))
    }

    // Hide loader and show canvas
    hideLoaderShowCanvas(): void {
        this.element.find('.house-3d-container').removeClass('house-3d-container--loading');
        this.renderer.domElement.style.display = 'block';
    }

    onWindowResize(): void {
        this.camera.aspect = this.element.children('.house-3d-container').innerWidth() / this.element.children('.house-3d-container').innerHeight();
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.element.children('.house-3d-container').innerWidth(), this.element.children('.house-3d-container').innerHeight());
        this.labelRenderer.setSize(this.element.children('.house-3d-container').innerWidth(), this.element.children('.house-3d-container').innerHeight());
    }

    animate(): void {
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
    }

    onApartmentClick(event: PointerEvent): void {
        const canvasBounds = this.renderer.domElement.getBoundingClientRect();

        this.mouse.x = ((event.clientX - canvasBounds.left) / (canvasBounds.right - canvasBounds.left)) * 2 - 1;
        this.mouse.y = - ((event.clientY - canvasBounds.top) / (canvasBounds.bottom - canvasBounds.top)) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);

        const apartmentIntersects = this.raycaster.intersectObjects(this.apartments);

        if (apartmentIntersects.length > 0) {
            const apartmentObject = apartmentIntersects[0].object as THREE.Object3D & {
                apartment?: {
                    permalink?: string;
                };
            };
            const apartment = apartmentObject.apartment;

            if (apartment?.permalink) {
                window.location.href = apartment.permalink;
            }
        }
    }

    onMouseMove(event: PointerEvent): void {
        if (event.isPrimary === false) {
            return;
        }

        const canvasBounds = this.renderer.domElement.getBoundingClientRect();

        this.mouse.x = ((event.clientX - canvasBounds.left) / (canvasBounds.right - canvasBounds.left)) * 2 - 1;
        this.mouse.y = - ((event.clientY - canvasBounds.top) / (canvasBounds.bottom - canvasBounds.top)) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);

        const apartmentIntersects = this.raycaster.intersectObjects(this.apartments);

        if (apartmentIntersects && apartmentIntersects.length > 0) {
            let intersectedObject;

            let previousObject = this.hoveringObject

            if (apartmentIntersects.length > 0) {
                intersectedObject = apartmentIntersects[0].object;
            } else {
                intersectedObject = this.hoveringObject;
            }

            if (previousObject && previousObject.id != intersectedObject.id) {
                document.dispatchEvent(new CustomEvent('leaveApartmentModelOverlay'));
            }

            if (intersectedObject) {
                this.onHoveringApartment(intersectedObject);
            }
        } else {
            this.resetHoveringState();
        }
    }

    onHoveringApartment(object: any, triggerEvent: boolean = true) {
        if (! object) {
            return;
        }

        let hoveringColor = '#608360';

        if (document.body.classList.contains('theme-beige')) {
            hoveringColor = '#956456';
        } else if (document.body.classList.contains('theme-yellow')) {
            hoveringColor = 'rgba(253, 138, 100, 0.5)';
        }

        object.material.opacity = 0.85;
        object.material.color.set(hoveringColor);

        if (this.hoveringObject && this.hoveringObject !== object) {
            this.hoveringObject.material.opacity = 0;
            this.hoveringObject.children[0].element.classList.remove('house-3d-tooltip--visible')
        }

        this.hoveringObject = object;

        this.showApartmentTooltip(this.hoveringObject, triggerEvent);
    }

    maybeRotateToApartment(event: CustomEvent) {
        let apartmentNum = event.detail.number
        let houseNumber = event.detail.houseNumber ? event.detail.houseNumber.toString().replace('/', '') : false

        if (apartmentNum) {
            let hoveringApartment = null

            if (houseNumber) {
                hoveringApartment = this.apartments.find(object => object.apartment.tooltipNum == apartmentNum && object.apartment.houseNumber == houseNumber)
            }

            if (! hoveringApartment) {
                hoveringApartment = this.apartments.find(object => object.apartment.tooltipNum == apartmentNum)
            }

            if (! hoveringApartment) {
                return;
            }

            this.onHoveringApartment(hoveringApartment, false);
            const startPosition = this.camera.position.clone();
            const startSpherical = new THREE.Spherical();
            startSpherical.setFromVector3(startPosition);

            const targetPosition = hoveringApartment.closestCamera;
            const targetSpherical = new THREE.Spherical();
            targetSpherical.setFromVector3(targetPosition);

            // Normalize theta to avoid full circle rotation
            let deltaTheta = targetSpherical.theta - startSpherical.theta;
            if (deltaTheta > Math.PI) {
                targetSpherical.theta -= Math.PI * 2;
            } else if (deltaTheta < -Math.PI) {
                targetSpherical.theta += Math.PI * 2;
            }

            if (startPosition.distanceTo(targetPosition) > 1) {
                const modelCenter = new THREE.Vector3();
                const initialRadius = startSpherical.radius;

                this.gsapMovement = gsap.to(startSpherical, {
                    theta: targetSpherical.theta,
                    phi: targetSpherical.phi,
                    duration: 1.5,
                    onUpdate: () => {
                        startSpherical.radius = initialRadius;
                        this.camera.position.setFromSpherical(startSpherical);
                        this.camera.lookAt(modelCenter);
                    },
                    onComplete: () => {
                        // Completed.
                        this.gsapMovement = null;
                    }
                });
            }
        }
    }

    maybeHideApartment(event: CustomEvent) {
        const apartmentNum = event.detail.number

        if (apartmentNum) {
            this.resetHoveringState();
        }
    }

    showApartmentTooltip(object: any, triggerEvent: boolean = true): void {
        object.children[0].element.classList.add('house-3d-tooltip--visible');
        this.element.find('.house-3d-container').addClass('house-3d-container--hovering-apartment');

        if (triggerEvent) {
            document.dispatchEvent(new CustomEvent('enterApartmentModelOverlay', {
                detail: {
                    number: object.apartment.tooltipNum,
                    houseNumber: object.apartment.houseNumber,
                    houseName: object.apartment.houseName,
                },
            }))
        }
    }

    positionTooltipOverApartment(object): void {
        const box = new THREE.Box3().setFromObject(object);
        const topCenter = new THREE.Vector3(
            (box.min.x + box.max.x) / 2,
            box.min.y - 15,
            (box.min.z + box.max.z) / 2,
        );

        object.children[0].position.copy(topCenter);
    }

    resetHoveringState(): void {
        if (this.hoveringObject) {
            this.hoveringObject.material.opacity = 0;
            this.hoveringObject.children[0].element.classList.remove('house-3d-tooltip--visible');

            this.hoveringObject = null;
        }

        this.element.find('.house-3d-container').removeClass('house-3d-container--hovering-apartment');
        document.dispatchEvent(new CustomEvent('leaveApartmentModelOverlay'));
    }
}
/* eslint-enable */
